/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useEmailTemplateForm.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the email-templates module.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { EmailTemplate } from '../api/emailTemplatesApi';
import { useI18n } from '../../../hooks/useI18n';

export const useEmailTemplateForm = (
    formData: Partial<EmailTemplate>,
    baseline: Partial<EmailTemplate>,
    mode: 'create' | 'edit',
    setFormData: React.Dispatch<React.SetStateAction<Partial<EmailTemplate>>>
) => {
    const { t } = useI18n();
    const [errors, setErrors] = useState<Record<string, string>>({});

    const hasChanges = useMemo(() => {
        if (mode === 'create') return true;
        return JSON.stringify(formData) !== JSON.stringify(baseline);
    }, [formData, baseline, mode]);

    const validateForm = useCallback(() => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = t('emailTemplates.validation.nameRequired');
        if (!formData.slug) newErrors.slug = t('emailTemplates.validation.slugRequired');
        if (!formData.subject) newErrors.subject = t('emailTemplates.validation.subjectRequired');
        if (!formData.htmlContent) newErrors.htmlContent = t('emailTemplates.validation.htmlRequired');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleInputChange = useCallback((field: keyof EmailTemplate, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    }, [errors, setFormData]);

    return { errors, hasChanges, validateForm, handleInputChange };
};
